import { JobRequestCustomV1 } from "@/lib/store/Realtime/message.types";
import {
	FieldTypeEnum,
	FormField,
} from "@/dentlab/src/pages/form-builder/types/formTypes";
import { OdontogramInMessage } from "./components/odontogram-in-message";
import {
	CustomFormFileType,
	ToothData,
	AppointmentData,
} from "@/dentconnect/src/pages/job-request/types";
import { useFileHandler } from "@/lib/hooks/FileHandler/useFileHandler";
import { FileGalleryInMessage } from "./components/file-in-message/file-gallery-in-message";
import { useFormsStore } from "@/dentlab/src/store/Forms";
import { useEffect, useRef } from "react";

export const JobRequestCustomV1Block: React.FC<{
	content: JobRequestCustomV1;
	messageOfCurrentOrg: boolean;
	onShareFile: (fileId: string) => void;
	onUnshareFile: (fileId: string) => void;
	senderOrgName?: string;
	isAdmin: boolean;
}> = ({
	content,
	messageOfCurrentOrg,
	onShareFile,
	onUnshareFile,
	senderOrgName,
	isAdmin,
}) => {
	const { files, sharedUntil } = useFileHandler(content.file_ids || []);
	const fetchedRef = useRef(false);

	const formsByOrgId = useFormsStore((state) => state.formsByOrgId);
	const fetchFormById = useFormsStore((state) => state.fetchFormById);

	// Find the form template for this message
	// formsByOrgId is Record<orgId, Record<formId, ExtendedFormTemplate>>
	const formTemplate = Object.values(formsByOrgId)
		.flatMap((orgForms) => Object.values(orgForms))
		.find((form: any) => form.id === content.customFormId);

	// Fetch form if not in store (only once per formId)
	useEffect(() => {
		if (!formTemplate && content.customFormId && !fetchedRef.current) {
			fetchedRef.current = true;
			fetchFormById(content.customFormId);
		}
	}, [formTemplate, content.customFormId, fetchFormById]);

	// Helper to render a single field
	const renderField = (fieldId: string, field?: FormField) => {
		const value = content.customFormData[fieldId];
		if (!value) return null;

		const fieldType = value.fieldType;
		const fieldValue = value.fieldValue;
		const fieldLabel =
			content.customFormReferences?.[fieldId] || field?.label || fieldId;

		if (fieldType === FieldTypeEnum.ODONTOGRAM) {
			return (
				<OdontogramInMessage
					key={fieldId}
					fieldId={fieldId}
					fieldValue={fieldValue as ToothData[]}
					references={content.customFormReferences}
					odontogramFields={field?.odontogramFields}
				/>
			);
		}

		if (fieldType === FieldTypeEnum.FILE) {
			const formFiles = fieldValue as CustomFormFileType[];

			return (
				<div key={fieldId} className="mb-1 flex flex-row gap-1">
					<span className="font-medium">{fieldLabel}: </span>
					<span>
						{formFiles.map((file, index) => (
							<span key={index}>
								{file.name}
								<br />
							</span>
						))}
					</span>
				</div>
			);
		}

		if (fieldType === FieldTypeEnum.APPOINTMENT) {
			const appointments = fieldValue as AppointmentData[];

			const formatValue = (subFieldId: string, value: string): string => {
				const subField = field?.appointmentFields?.find(
					(f) => f.id === subFieldId
				);
				if (!subField || !value) return value;

				if (subField.type === "date") {
					try {
						const date = new Date(value);
						if (!isNaN(date.getTime())) {
							return date.toLocaleDateString("de-DE", {
								day: "2-digit",
								month: "2-digit",
								year: "numeric",
							});
						}
					} catch (e) {
						return value;
					}
				}

				if (subField.type === "time") {
					return value ? `${value} Uhr` : value;
				}

				return value;
			};

			return (
				<div key={fieldId} className="mb-2">
					<span className="font-medium">{fieldLabel}:</span>
					<div className="ml-4 mt-1">
						{appointments?.map((appointment, index) => (
							<div
								key={appointment.appointmentId}
								className="mb-2 pb-2 border-b last:border-b-0"
							>
								<div className="font-medium text-sm">
									Termin {index + 1}:
								</div>
								<div className="ml-2 mt-1 text-sm">
									{Object.entries(appointment.values).map(
										([subFieldId, value]) => {
											const subFieldLabel =
												content.customFormReferences?.[
													subFieldId
												] || subFieldId;
											const formattedValue = formatValue(
												subFieldId,
												value
											);
											return (
												<div
													key={subFieldId}
													className="mb-0.5"
												>
													<span className="font-medium">
														{subFieldLabel}:{" "}
													</span>
													<span>
														{formattedValue}
													</span>
												</div>
											);
										}
									)}
								</div>
							</div>
						))}
					</div>
				</div>
			);
		}

		return (
			<div key={fieldId} className="mb-1">
				<span className="font-medium">{fieldLabel}: </span>
				<span>
					{Array.isArray(fieldValue)
						? fieldValue.join(", ")
						: (fieldValue as string)?.toString() || ""}
				</span>
			</div>
		);
	};

	// Create a formatted display of the custom form data organized by sections
	const formattedData = () => {
		if (!content.customFormData) return null;

		// If we have the form template, render fields in order by sections
		if (formTemplate && (formTemplate as any).definition?.sections) {
			return (
				<div className="mt-2">
					{(formTemplate as any).definition.sections.map(
						(section: any, sectionIndex: number) => (
							<div key={sectionIndex} className="mb-4">
								{section.title && (
									<div className="text-sm font-semibold text-gray-800 mb-2">
										{section.title}
									</div>
								)}
								<div className="text-sm text-gray-700">
									{section.fields.map((field: any) =>
										renderField(field.id, field)
									)}
								</div>
							</div>
						)
					)}
				</div>
			);
		}

		// Fallback to original unordered display if form template not available
		return (
			<div className="mt-2">
				<div className="text-sm text-gray-700">
					{Object.keys(content.customFormData).map((fieldId) =>
						renderField(fieldId)
					)}
				</div>
			</div>
		);
	};

	return (
		<div className="flex flex-col gap-3">
			<div className="text-lg font-semibold text-gray-700">
				Neue Auftragsanfrage
			</div>
			<div className="flex flex-col gap-2">
				<div className="flex items-start gap-2">
					<span className="text-sm font-medium text-gray-600">
						Datum:
					</span>
					<span className="text-sm text-gray-700">
						{content.incoming_order_date
							? new Date(
									content.incoming_order_date
								).toLocaleDateString("de-DE", {
									day: "2-digit",
									month: "2-digit",
									year: "numeric",
									hour: "2-digit",
									minute: "2-digit",
								})
							: "Kein Datum"}
					</span>
				</div>
				<div className="flex items-start gap-2">
					<span className="text-sm font-medium text-gray-600">
						Formular-Name:
					</span>
					<span className="text-sm text-gray-700">
						{content?.customFormTitle}
					</span>
				</div>

				{/* Show formatted form data */}
				{formattedData()}
				{files && files.length > 0 && (
					<div className="flex flex-col gap-1">
						<span className="text-sm text-gray-700 mb-2">
							Dateien:
						</span>
						<div className="flex flex-col w-full justify-center">
							<FileGalleryInMessage
								files={files}
								messageOfCurrentOrg={messageOfCurrentOrg}
								sharedUntil={sharedUntil}
								onShareFile={onShareFile}
								onUnshareFile={onUnshareFile}
								isAdmin={isAdmin}
							/>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};
